import React, { useEffect, useRef, useState, useContext } from 'react'
import { gql, useMutation } from '@apollo/client'
import Modal from 'styled-react-modal'
import styled, { useTheme } from 'styled-components'
import { FormattedMessage } from "react-intl"

import {
  Button,
  Input,
} from '../StyledComponents'
import {
  Wrapper,
} from '../StyledComponents'
import { loggedInContext } from '../auth/AuthContext'
import { getAuthErrorCode, toEnglishAuthError } from '../auth/authErrorMessage'
import { useCsEvent } from '../client/useEvents'
import { labelFadeTransition } from '../styles/motion'

// Google One Tap types
interface CredentialResponse {
  credential: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id?: string
            callback: (response: CredentialResponse) => void
            cancel_on_tap_outside?: boolean
            context?: string
          }) => void
          prompt: () => void
        }
      }
    }
    _googleOneTapInitialized?: boolean
  }
}

interface SignupModalProps {
  onClose?: () => void
}

interface SignupMutationData {
  signup: {
    token: string
  }
}

interface SignupMutationVariables {
  username: string
  password: string
  email: string
}

interface GoogleAuthMutationData {
  googleAuth: {
    token: string
  }
}

interface GoogleAuthMutationVariables {
  credential: string
}

interface LoggedInContextValue {
  logIn: (token: string) => void
  openLoginModal: (cta?: React.ReactNode) => void
}

// Google One Tap script loader
function loadGoogleScript(cb?: () => void): void {
  if (window.google && window.google.accounts) {
    cb?.()
    return
  }
  const script = document.createElement('script')
  script.src = 'https://accounts.google.com/gsi/client'
  script.async = true
  script.defer = true
  script.onload = () => cb?.()
  document.body.appendChild(script)
}

const StyledModal = Modal.styled`
  margin-left: auto;
  margin-right: auto;
  margin-top: 3em;
  background: ${({ theme }) => theme.colors.cardBg};
  border-radius: 18px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.18);
  border: none;
  padding: 2.5em 2em 2em 2em;
  width: 95vw;
  max-width: 420px;
  transition: box-shadow 0.2s;
  @media(min-width: 960px) {
    width: 420px;
  }
`
const Row = styled.div`
  position: relative;
  margin-top: 1.2em;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 340px;
  @media(min-width: 960px) {
    max-width: 340px;
  }
`
const FormButton = styled(Button)`
  font-family: NeueEinstellungBold;
  text-align: center;
  white-space: nowrap;
  line-height: 1.5;
  font-size: 1.15rem;
  background: linear-gradient(90deg, #8b7d65 0%, #bcae8a 100%);
  border: none;
  color: #fff;
  outline: 0px;
  display: block;
  font-weight: 600;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(139, 125, 101, 0.08);
  padding: 0.9em 0;
  margin-bottom: 0.5em;
  letter-spacing: 0.01em;
  transition: background 0.2s, box-shadow 0.2s;
  cursor: pointer;
  &:hover, &:focus {
    background: linear-gradient(90deg, #bcae8a 0%, #8b7d65 100%);
    box-shadow: 0 4px 16px rgba(139, 125, 101, 0.13);
  }
`
const FormInput = styled(Input)`
  font-family: NeueEinstellungRegular;
  padding: 1.1em 1em 1.1em 1em;
  width: 100%;
  outline: 0px;
  border: 1.5px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 7px;
  font-size: 1.1rem;
  appearance: none;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.inputBg};
  color: ${({ theme }) => theme.colors.inputText};
  margin-bottom: 0.2em;
  transition: border 0.2s, box-shadow 0.2s;
  &:focus {
    border: 1.5px solid ${({ theme }) => theme.colors.sectionAccent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.surfaceHover};
  }
`
const FormLabel = styled.label<{ $hide?: boolean }>`
  position: absolute;
  left: 1.2em;
  top: 1.1em;
  font-size: 0.93rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: NeueEinstellungRegular;
  background: transparent;
  line-height: 1.5;
  pointer-events: none;
  ${labelFadeTransition}
  z-index: 2;
  opacity: ${({ $hide }) => $hide ? 0 : 1};
`
const AltRow = styled.div`
`
const ErrorDiv = styled.div`
  font-family: NeueEinstellungRegular;
  font-size: 1rem;
  line-height: 1.3;
  padding: 0.9em 1em;
  position: relative;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(219, 41, 41, 0.08);
  text-align: center;
`
const mutation = gql`
  mutation Signup($username: String!, $password: String!, $email: String!) {
    signup(username: $username, password: $password, email: $email) {
      token
    }
  }
`

const googleAuthMutation = gql`
  mutation GoogleAuth($credential: String!) {
    googleAuth(credential: $credential) {
      token
    }
  }
`

function SignupModal(props: SignupModalProps) {
  const theme = useTheme()
  const usernameInput = useRef<HTMLInputElement>(null)
  const emailInput = useRef<HTMLInputElement>(null)
  const passwordInput = useRef<HTMLInputElement>(null)
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const context = useContext(loggedInContext) as LoggedInContextValue
  const [errorDisplay, setErrorDisplay] = useState<string | null>(null)
  const fireCsEvent = useCsEvent()

  const [googleAuth] = useMutation<GoogleAuthMutationData, GoogleAuthMutationVariables>(googleAuthMutation, {
    onCompleted: (data) => {
      if (data?.googleAuth?.token) {
        fireCsEvent({
          category: 'Signup',
          action: 'Eylem',
          label: 'Signup completed',
        })
        context.logIn(data.googleAuth.token)
        closeModal()
      }
    },
    onError: (err) => {
      setErrorDisplay(toEnglishAuthError(err.message))
    },
    errorPolicy: 'all'
  })

  const [signup,
    { called: signupCalled, loading: signupLoading, data: signupData, error: signupError }
  ] = useMutation<SignupMutationData, SignupMutationVariables>(mutation, {
    onCompleted: (data) => {
      if (data?.signup?.token) {
        console.info("Signup completed with token")
        fireCsEvent({
          category: 'Signup',
          action: 'Eylem',
          label: 'Signup completed',
        })
        context.logIn(data.signup.token)
        closeModal()
      }
    },
    errorPolicy: 'all'
  })

  useEffect(() => {
    const translatedSignupError = toEnglishAuthError(signupError?.message)
    if ((!errorDisplay && translatedSignupError)
      || (translatedSignupError && errorDisplay !== translatedSignupError)) {
      fireCsEvent({
        category: "Signup",
        action: "Eylem",
        label: `Signup error: ${getAuthErrorCode(signupError?.message) ?? 'unknown'}`,
      })
      setErrorDisplay(translatedSignupError)
    }
  }, [signupError, errorDisplay, fireCsEvent])

  console.debug("[SignupModal] render", "errors:", signupError)

  function afterOpenModal() {
    // references are now sync'd and can be accessed.
  }

  function closeModal() {
    props.onClose?.()
  }

  const onKaydol = () => {
    fireCsEvent({
      category: "Signup",
      action: "Eylem",
      label: "Form submit"
    })
    setErrorDisplay(null)
    signup({
      variables: {
        username,
        password,
        email
      }
    })
  }

  const onGirisYapClicked = () => {
    context.openLoginModal()
  }

  function handleGoogleOneTapInit() {
    fireCsEvent({
      category: "Signup",
      action: "Eylem",
      label: "Google buttona basildi"
    })
    loadGoogleScript(() => {
      if (window.google && window.google.accounts && !window._googleOneTapInitialized) {
        window._googleOneTapInitialized = true
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: (response: CredentialResponse) => {
            fireCsEvent({
              category: "Signup",
              action: "Eylem",
              label: "Google One Tap response"
            })
            googleAuth({ variables: { credential: response.credential } })
          },
          cancel_on_tap_outside: false,
          context: 'signup',
        })
        window.google.accounts.id.prompt()
      }
    })
  }

  useEffect(() => {
    fireCsEvent({
      category: "Signup",
      action: "Eylem",
      label: "Signup modal görüldü"
    })
    loadGoogleScript()
  }, [fireCsEvent])

  return (
    <StyledModal
      isOpen={true}
      afterOpen={afterOpenModal}
      onEscapeKeydown={closeModal}
      onBackgroundClick={closeModal}
      contentLabel="Signup Popup"
    >
      <Wrapper>
        <h1 className="text-center" style={{ fontFamily: 'NeueEinstellungBold', fontSize: '2.1rem', margin: '0.2em 0 0.7em 0', color: theme.colors.sectionAccent, letterSpacing: '0.01em' }}>
          <FormattedMessage id="login.uye_ol" />
        </h1>
        {errorDisplay && <Row>
          <ErrorDiv>{errorDisplay}</ErrorDiv>
        </Row>}
        <Row className="d-flex justify-content-center">
          <FormButton
            style={{
              background: theme.colors.cardBg,
              color: theme.colors.text,
              border: `1.5px solid ${theme.colors.border}`,
              boxShadow: '0 2px 8px rgba(66,133,244,0.07)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '1.08rem',
              gap: 8,
              marginBottom: 0,
              marginTop: 0,
              padding: '0.7em 0',
              borderRadius: 8,
              transition: 'box-shadow 0.2s',
            }}
            onClick={handleGoogleOneTapInit}
          >
            <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" style={{ height: 22, marginRight: 10, verticalAlign: 'middle', borderRadius: 2 }} />
            <span style={{ verticalAlign: 'middle', color: theme.colors.text, fontWeight: 600 }}>
              <FormattedMessage id="login.google" defaultMessage="Sign in with Google" />
            </span>
          </FormButton>
        </Row>
        <Row style={{ alignItems: 'center', margin: '1.7em 0 1.1em 0', width: '100%', maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ flex: 1, height: 1, background: theme.colors.border }}></div>
          <span style={{ margin: '0 1em', color: theme.colors.sectionAccent, fontSize: '1.08em', whiteSpace: 'nowrap', display: 'inline-block', minWidth: 40, textAlign: 'center', fontWeight: 600, letterSpacing: '0.01em' }}>
            <FormattedMessage id="login.or" defaultMessage="or" />
          </span>
          <div style={{ flex: 1, height: 1, background: theme.colors.border }}></div>
        </Row>
        <Row>
          <FormInput
            ref={usernameInput}
            type="text"
            autoCapitalize="none"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder=""
          />
          <FormLabel $hide={!!username}><FormattedMessage id="login.k_adi" /></FormLabel>
        </Row>
        <Row>
          <FormInput
            ref={emailInput}
            type="text"
            autoCapitalize="none"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder=""
          />
          <FormLabel $hide={!!email}><FormattedMessage id="signup.email" /></FormLabel>
        </Row>
        <Row>
          <FormInput
            ref={passwordInput}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder=""
          />
          <FormLabel $hide={!!password}><FormattedMessage id="login.sifre" /></FormLabel>
        </Row>
        <Row className='d-flex justify-content-center'>
          <FormButton onClick={onKaydol} style={{ marginTop: '0.7em', marginBottom: 0 }}>
            <FormattedMessage id="login.uye_ol" />
          </FormButton>
        </Row>
        <AltRow className="mt-1">
          <h3 className="text-center" style={{ fontFamily: 'NeueEinstellungBold', fontSize: '1.1rem', color: theme.colors.sectionAccent, margin: '1.2em 0 0.5em 0', letterSpacing: '0.01em' }}>
            <FormattedMessage id="signup.login_cta" />
          </h3>
          <Row className="d-flex justify-content-center">
            <FormButton onClick={onGirisYapClicked} style={{ background: theme.colors.cardBg, color: theme.colors.sectionAccent, border: `1.5px solid ${theme.colors.sectionAccent}`, marginTop: 0 }}>
              <FormattedMessage id="signup.login" />
            </FormButton>
          </Row>
        </AltRow>
      </Wrapper>
    </StyledModal>
  )
}

export default SignupModal
